import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import {
  ShoppingBag, Star, Bell, ChefHat,
  LayoutGrid, UserCog, MapPin, ShieldAlert,
} from 'lucide-react';
import { fetchMe, getStoredUser } from '../../services/auth.api';
import OverviewTab from './tabs/OverviewTab';
import EditProfileTab from './tabs/EditProfileTab';
import AddressBookTab from './tabs/AddressBookTab';
import AccountSettingsTab from './tabs/AccountSettingsTab';
import './styles/profile.css';

const fmtMemberSince = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

function Avatar({ name, src }) {
  const initial = name ? name.trim()[0].toUpperCase() : '?';
  return (
    <div className="prof-avatar-ring">
      {src
        ? <img className="prof-avatar-img" src={src} alt={name || 'Profile'} />
        : <div className="prof-avatar-initials">{initial}</div>
      }
    </div>
  );
}

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid },
  { id: 'edit', label: 'Edit Profile', icon: UserCog },
  { id: 'addresses', label: 'Addresses', icon: MapPin },
  { id: 'settings', label: 'Account Settings', icon: ShieldAlert },
];

export default function MyProfile() {
  // Render the cached account immediately, then refresh from the API — the
  // login response is lean and carries no dateOfBirth/avatar/createdAt.
  const [user, setUser] = useState(() => getStoredUser());
  const [tab, setTab] = useState('overview');
  const loading = !user;

  useEffect(() => {
    fetchMe().then(setUser).catch(() => { /* keep the cached account */ });
  }, []);

  return (
    <div className="home-content-shell page-navbar-space pb-5">
      <div className="prof-shell">

        {/* ── Hero ── */}
        <div className="prof-hero">
          {loading
            ? <Skeleton circle width={96} height={96} style={{ marginBottom: 12 }} />
            : <Avatar name={user?.name} src={user?.avatar} />
          }

          {loading
            ? <Skeleton height={26} width={180} style={{ marginBottom: 4 }} />
            : <h1 style={{ fontWeight: 800, fontSize: 22, margin: 0, color: '#0f172a', fontFamily: "'Inter', sans-serif" }}>{user?.name}</h1>
          }

          {loading
            ? <Skeleton height={14} width={120} />
            : <p style={{ margin: 0, fontSize: 13, color: '#9ca3af', fontFamily: "'Inter', sans-serif" }}>
                {user?.email || user?.phone}
              </p>
          }

          {!loading && user?.createdAt && (
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#b0b7c3', fontFamily: "'Inter', sans-serif" }}>
              Member since {fmtMemberSince(user.createdAt)}
            </p>
          )}

          {/* Quick links */}
          <div className="prof-quick-links">
            <Link to="/my-orders" className="prof-quick-btn prof-quick-btn-primary">
              <ShoppingBag size={14} /> My Orders
            </Link>
            <Link to="/my-wishes" className="prof-quick-btn prof-quick-btn-outline">
              <Star size={14} /> My Wishes
            </Link>
            <Link to="/notifications" className="prof-quick-btn prof-quick-btn-outline">
              <Bell size={14} /> Notifications
            </Link>
            <Link to="/all-kitchens" className="prof-quick-btn prof-quick-btn-outline">
              <ChefHat size={14} /> Browse Kitchens
            </Link>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="prof-tabbar">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                className={`prof-tab${tab === t.id ? ' prof-tab-active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="prof-tab-panel">
          {tab === 'overview' && <OverviewTab user={user} loading={loading} />}
          {tab === 'edit' && <EditProfileTab user={user} loading={loading} onUpdated={setUser} />}
          {tab === 'addresses' && <AddressBookTab />}
          {tab === 'settings' && <AccountSettingsTab />}
        </div>

      </div>
    </div>
  );
}
